import {
  Alert,
  Divider,
  ExclamationTriangleIcon,
  Flex,
  Skeleton,
  Text,
} from "@fluentui/react-northstar";
import { useEffect, useState } from "react";

import { useSurvey } from "../../../../../_shared/actions/useSurvey";
import { useUserInfo } from "../../../../../_shared/actions/useUserInfo";
import { useTeamsContext } from "../../../../../_shared/state/TeamsContextProvider";
import { useInterval } from "../../../../../_shared/utils/useInterval";
import { SurveyMeetingStatePill } from "../SurveyMeetingStatePill";
import { SurveyTypePill } from "../SurveyTypePill";
import { IcebreakerAnswerForm } from "./IcebreakerAnswerForm";
import { IcebreakerResponses } from "./IcebreakerResponses";

const TRACKING = "meeting-bubble";

export const IcebreakerInformation: React.FC<{ surveyId: string; maxRespondents?: number }> = ({
  surveyId,
}) => {
  const { teamsContext } = useTeamsContext();
  const { user } = useUserInfo();
  const { survey, isSurveyLoading, loadSurvey, surveyError } = useSurvey({ loadResponses: true });
  const [hasAnswered, setHasAnswered] = useState<boolean>(false);

  useEffect(() => {
    loadSurvey(teamsContext, surveyId);
  }, [surveyId, loadSurvey, teamsContext]);

  useInterval(() => {
    if (!isSurveyLoading && survey?.state !== "closed") {
      loadSurvey(teamsContext, surveyId);
    }
  }, 1000);

  useEffect(() => {
    if (survey?.responses && survey.responses.some(r => r.user.aadObjectId === user.userId)) {
      setHasAnswered(true);
    }
  }, [survey, user]);

  if (surveyError) {
    return (
      <Alert
        danger
        icon={<ExclamationTriangleIcon />}
        content="There was a problem loading the Icebreaker"
      />
    );
  }

  if (!survey || !user) {
    return (
      <Skeleton animation="wave">
        <Flex column gap="gap.small">
          <Skeleton.Shape width="100%" />
          <Skeleton.Button />
        </Flex>
      </Skeleton>
    );
  }

  const icebreakerQuestion = survey.questions[0].label;

  const responses = survey.responses ?? [];
  const totalResponses = responses.length;

  return (
    <Flex column gap="gap.small">
      <Flex gap="gap.smaller" vAlign="center">
        <SurveyTypePill survey={survey} />
        <SurveyMeetingStatePill survey={survey} />
      </Flex>

      <Text content={`💬 ${icebreakerQuestion}`} size="large" />

      {survey.state !== "open" && <Alert content="This Icebreaker is no longer open" warning />}
      {survey.state === "open" && (
        <IcebreakerAnswerForm
          survey={survey}
          trackingSource={TRACKING}
          hasCompleted={hasAnswered}
          onCompleted={() => {
            setHasAnswered(true);
          }}
        />
      )}

      <Divider />

      <IcebreakerResponses
        survey={survey}
        responses={responses}
        totalResponses={totalResponses}
        onReactionChange={() => {
          loadSurvey(teamsContext, surveyId);
        }}
      />
    </Flex>
  );
};
